import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../services/api';
import { useAuthStore } from '../stores/auth.store';
import { useUILocale } from '../hooks/useUILocale';
import { withLocale } from '../locale';

const APPLE_SERVICE_ID = 'fun.storychain.app';
const APPLE_REDIRECT_URI = 'https://api.storychain.fun/auth/apple/callback';
const GOOGLE_CLIENT_ID =
  '15841512328-rb3275e6rp2m2gne0osfmen60tl9hcqi.apps.googleusercontent.com';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const uiLocale = useUILocale();
  const setAuth = useAuthStore((state) => state.setAuth);

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // Pick up an Apple-redirect JWT out of the URL hash (#provider=apple&token=…)
  // and land the user on the post-login page.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const params = new URLSearchParams(hash);
    if (params.get('provider') !== 'apple') return;
    const token = params.get('token');
    if (!token) return;

    (async () => {
      try {
        const me = await api.fetchMeWithToken(token);
        setAuth(token, me.user);
        const pendingJoinCode = localStorage.getItem('pendingJoinCode');
        navigate(withLocale(uiLocale, pendingJoinCode ? `/join/${pendingJoinCode}` : '/'), { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Apple sign-in failed');
      } finally {
        history.replaceState(null, '', window.location.pathname);
      }
    })();
  }, [navigate, setAuth]);

  // Google Identity Services — load script lazily, render the button
  useEffect(() => {
    const existing = document.getElementById('gsi-script');
    if (existing) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client?hl=en';
    script.async = true;
    script.id = 'gsi-script';
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const google = (window as unknown as { google?: { accounts?: { id?: { initialize: (c: unknown) => void; renderButton: (el: HTMLElement, c: unknown) => void } } } }).google;
      if (!google?.accounts?.id || !googleButtonRef.current) return;
      window.clearInterval(interval);
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: { credential?: string }) => {
          if (!response.credential) return;
          try {
            const res = await api.loginWithGoogle(response.credential);
            setAuth(res.token, res.user);
            const pendingJoinCode = localStorage.getItem('pendingJoinCode');
            navigate(withLocale(uiLocale, pendingJoinCode ? `/join/${pendingJoinCode}` : '/'), { replace: true });
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Google sign-in failed');
          }
        },
      });
      google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'pill',
        logo_alignment: 'left',
        locale: 'en',
        width: 320,
      });
    }, 200);
    return () => window.clearInterval(interval);
  }, [navigate, setAuth]);

  const handleAppleLogin = () => {
    const params = new URLSearchParams({
      client_id: APPLE_SERVICE_ID,
      redirect_uri: APPLE_REDIRECT_URI,
      response_type: 'code id_token',
      response_mode: 'form_post',
      scope: 'name email',
    });
    window.location.href = `https://appleid.apple.com/auth/authorize?${params}`;
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.requestCode(email);
      setStep('code');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.verifyCode(email, code);

      const pendingJoinCode = localStorage.getItem('pendingJoinCode');
      const redirectPath = withLocale(uiLocale, pendingJoinCode ? `/join/${pendingJoinCode}` : '/');

      setAuth(response.token, response.user);
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Sign In">
      <div className="flex flex-col items-center justify-center min-h-[70vh]">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <span className="text-6xl">📖</span>
            <h1 className="text-2xl font-bold mt-4">Welcome to StoryChain</h1>
            <p className="text-gray-500 mt-2">Sign in to continue</p>
          </div>

          {step === 'email' && (
            <div className="space-y-3 mb-6">
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={handleAppleLogin}
                  style={{ width: 320, height: 40 }}
                  className="relative bg-white text-gray-800 border border-gray-300 rounded-full text-sm font-medium hover:bg-gray-50 transition"
                >
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  <span>Sign in with Apple</span>
                </button>
              </div>
              <div ref={googleButtonRef} className="flex justify-center" />
              <div className="flex items-center gap-3 text-gray-400 text-xs">
                <div className="flex-1 border-t border-gray-200" />
                <span>or</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
            </div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email.includes('@')}
                className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 transition"
              >
                {loading ? 'Sending...' : 'Get Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <p className="text-center text-gray-600 mb-4">
                Enter the code sent to {email}
              </p>

              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-2xl tracking-widest focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  maxLength={6}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full bg-primary-600 text-white py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 transition"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full text-primary-600 py-2 font-medium"
              >
                Change email
              </button>
            </form>
          )}

          {error && (
            <p className="text-red-500 text-center mt-4">{error}</p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LoginPage;
